import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import InputField from 'components/InputField';
import validations from 'validations';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { Button, Loader } from 'ncoded-component-library';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { isEmail } from 'utils';
import { getAddGroupPayloadValues } from './utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DataRoomMembers } from 'router/subrouters/DataRoom/models/DataRoomMember';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';

import './BulkAddMember.styles.scss';

export type MainGroupInfoForm = {
  name: string;
  buySideGroupMembers: string[];
  buySideGroupMembersUnsubmited?: string;
};

export type AddGroupForm = MainGroupInfoForm & {
  grantFullAccess?: boolean;
};

type BulkAddMemberProps = {
  className?: string;
  updateMembers: (data: DataRoomMembers) => void;
};

const BulkAddMember: React.FC<BulkAddMemberProps> = (props) => {
  const { className, updateMembers } = props;

  const classes = classNames('bulk-add-member', className);

  const { t } = useTranslation();

  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const initialValues = useMemo(
    () =>
      ({
        buySideGroupMembers: [],
      } as const),
    [],
  );

  const onSubmit = useCallback(
    async (values: AddGroupForm) => {
      try {
        const { data } = await dataRoomApi.members.createBuySideGroup(
          _id,
          getAddGroupPayloadValues(values),
        );

        popSuccess(t('successfullyAddedBuySideGroup'));
        updateMembers(data);
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, t, updateMembers, popServerError, popSuccess],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({
        invalid,
        submitting,
        values: { buySideGroupMembers, buySideGroupMembersUnsubmited },
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          <Field
            name="name"
            label={t('groupName')}
            description={t('helpColleaguesUnderstandRepresents')}
            component={InputField}
            validate={validations.required()}
            required
          />

          <Field
            name="buySideGroupMembers"
            label={t('inviteBuyMembers')}
            component={TextareaEmailsField}
            description={`${t('buySideGroupNote')} ${t(
              'weAreGoingToSendInvitationsViaEmail',
            )}`}
            required
            validate={validations.required()}
          />

          <Field
            name="grantFullAccess"
            component={CheckboxField}
            type="checkbox"
            label={t('grantAccessToAllFiles')}
          />

          <div className="actions">
            <Button
              type="submit"
              variant="outline"
              iconPosition="right"
              icon={ArrowrightIcon}
              disabled={
                invalid ||
                (!buySideGroupMembers.length &&
                  !buySideGroupMembersUnsubmited) ||
                (buySideGroupMembersUnsubmited &&
                  !isEmail(buySideGroupMembersUnsubmited))
              }
            >
              {t('sendInvites')}
            </Button>
          </div>

          {submitting && <Loader />}
        </form>
      )}
    />
  );
};

export default BulkAddMember;
